import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * For a vet user: returns true when this conversation contains a participant
 * the vet's practice has blocked. Returns false for non-vet users or when
 * nobody in the conversation is blocked. Used to disable composer + invite
 * actions across chat surfaces.
 */
export function useBlockedConversation(conversationId: string | null | undefined, userId: string | null | undefined) {
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!conversationId || !userId) {
      setBlocked(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      // Find practice(s) owned by this user
      const { data: practices } = await supabase
        .from("vet_practices")
        .select("id")
        .eq("user_id", userId);
      const practiceIds = (practices || []).map((p) => p.id);
      if (practiceIds.length === 0) {
        if (!cancelled) {
          setBlocked(false);
          setLoading(false);
        }
        return;
      }

      const { data: parts } = await supabase
        .from("conversation_participants")
        .select("user_id")
        .eq("conversation_id", conversationId);
      const otherIds = (parts || []).map((p) => p.user_id).filter((id) => id !== userId);
      if (otherIds.length === 0) {
        if (!cancelled) {
          setBlocked(false);
          setLoading(false);
        }
        return;
      }

      const { data: blocks } = await supabase
        .from("vet_blocked_owners")
        .select("owner_user_id")
        .in("practice_id", practiceIds)
        .in("owner_user_id", otherIds);
      if (!cancelled) {
        setBlocked((blocks || []).length > 0);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId, userId]);

  return { blocked, loading };
}
